import { useCallback, useEffect, useState } from 'react';
import { log } from '../../app/logger';
import { loadRoster } from '../../app/roster';
import { isRecordedShape, loadSettings, parseTeamShape } from '../../app/settingsB';
import { config } from '../../config';
import { credentialStateB } from './moreB';
import type { CredentialStateB } from './moreB';
import { lockNudgeMinFromB } from '../lockNudge';
import { applyThemeB, themeChoiceFromB } from '../theme';
import type { ThemeChoiceB } from '../theme';

/**
 * B-19 — the More tab's controller (design spec §2.4's More row).
 *
 * `useHomeB`/`useRoster`'s shape kept: one read on mount, an exposed `refresh` every write's
 * return path calls, and a completed-reads counter rendered as `data-read` so a browser pass
 * waits on a number instead of a sleep. *"Committed is not rendered"* is this project's most
 * repeated defect shape, and settings are no exception — what the screen shows about the stored
 * credential is always a re-read, never the screen's belief about its own write.
 *
 * ── The credential never becomes React state ─────────────────────────────────
 *
 * `loadSettings()` returns the stored value, and this hook converts it to a
 * {@link CredentialStateB} **inside the async, before any `setState`**. The secret therefore
 * exists as a local for the length of one expression and is never held in a component, never
 * flows into a `value` attribute, never lands in a React DevTools tree, and cannot appear in a
 * screenshot. §2.6's field writes it; nothing reads it back but `app/coach`.
 */

const MODULE = 'ui-b/more';

type Phase = 'loading' | 'ready' | 'failed';

export interface MoreState {
  readonly phase: Phase;
  /** Whether a credential is stored, and how long it is. Never the credential. */
  readonly credential: CredentialStateB;
  /** The stored theme choice, or `null` — the device's own setting is being followed. */
  readonly theme: ThemeChoiceB | null;
  /**
   * B-19a — LOCK-1's configurable minute, **as parsed**, never the raw stored value. The screen
   * therefore shows the number the nudge is actually using: a row holding `999` renders as the
   * default here, not as `999` beside a nudge quietly firing at 15′.
   */
  readonly lockNudgeMin: number;
  /**
   * B §15.6 (B-25) — whether a `teamShape` row exists, which is the fact the import plan preview
   * needs to state its replace-all consequence. Deliberately a **boolean, not the shape**: this
   * screen renders nothing about the shape itself (My Team owns that), and carrying the value here
   * would put a tactical profile into the same component state as the credential card for no
   * reader. **B-26's `parseTeamShape` has now refined "exists" to "is non-empty"**: a row holding a
   * corrupt or all-blank value is nothing to lose, so warning about it would be a false alarm.
   */
  readonly shapeStored: boolean;
  /**
   * §15.6 — how many roster players an import would remove. Needed because the ROSTER's replace-all
   * sentence names the count in the user's terms (*"Your 16 roster players will be removed and 0
   * restored"*), and `plan.willRemove` cannot supply it before the file is even picked.
   */
  readonly rosterCount: number;
  /** Completed reads — incremented after the state it describes. */
  readonly reads: number;
}

export interface MoreActions {
  readonly refresh: () => void;
}

export function useMore() {
  const [phase, setPhase] = useState<Phase>('loading');
  const [credential, setCredential] = useState<CredentialStateB>({ stored: false, length: 0 });
  const [theme, setTheme] = useState<ThemeChoiceB | null>(null);
  const [lockNudgeMin, setLockNudgeMin] = useState(config.LOCK_NUDGE_MIN);
  const [shapeStored, setShapeStored] = useState(false);
  const [rosterCount, setRosterCount] = useState(0);
  const [reads, setReads] = useState(0);

  const refresh = useCallback((): void => {
    void (async () => {
      const [settings, roster] = await Promise.all([loadSettings(), loadRoster()]);
      setRosterCount(roster.length);
      const choice = themeChoiceFromB(settings.themePreference);
      setCredential(credentialStateB(settings.backupCredential));
      setTheme(choice);
      setLockNudgeMin(lockNudgeMinFromB(settings.lockNudgeMin));
      // Non-empty, not merely present: `parseTeamShape` degrades a corrupt or foreign value to
      // the empty shape, and an empty shape is nothing an import can take away.
      setShapeStored(isRecordedShape(parseTeamShape(settings.teamShape)));
      /**
       * Applied here, on the READ, rather than on the theme button's write — so the document
       * attribute is always a re-read of what storage holds. It matters for a case the write
       * path cannot cover at all: an **import** wipes the settings table, and `themePreference`
       * is denied in both directions so no archive can put it back. Without this line the
       * document would keep rendering the choice of a preference that no longer exists.
       */
      applyThemeB(choice);
      setPhase('ready');
      setReads((count) => count + 1);
    })().catch((cause: unknown) => {
      log.error(MODULE, 'B §2.4: settings could not be read', {
        message: cause instanceof Error ? cause.message : String(cause),
      });
      setPhase('failed');
    });
  }, []);

  useEffect(refresh, [refresh]);

  return {
    state: {
      phase,
      credential,
      theme,
      lockNudgeMin,
      shapeStored,
      rosterCount,
      reads,
    } satisfies MoreState,
    actions: { refresh } satisfies MoreActions,
  };
}
